import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateSiteDto,
  ImportSiteFromCrmDto,
  SiteDto,
  SiteListsControllerFindSitesForSiteListParams,
  SitesControllerFindAllParams,
  UpdateSiteDto,
} from '@/api/generated/schemas';
import { siteListsControllerFindSitesForSiteList } from '@/api/generated/site-lists/site-lists';
import {
  sitesControllerBatchMoveSitesToList,
  sitesControllerCheckNmiExists,
  sitesControllerCreate,
  sitesControllerFindAll,
  sitesControllerFindOne,
  sitesControllerFindSiteListsForSite,
  sitesControllerImportFromCrm,
  sitesControllerMoveSiteToList,
  sitesControllerRemove,
  sitesControllerUpdate,
} from '@/api/generated/sites/sites';

// Query Keys Factory
export const sitesKeys = {
  all: ['sites'] as const,
  list: (params?: SitesControllerFindAllParams) => [...sitesKeys.all, 'list', params] as const,
  // Prefix shared by every paginated/searched variant — use for invalidation so
  // all param combinations refetch (passing a params-specific key would miss them).
  bySiteListAll: (siteListId: string) => [...sitesKeys.all, 'bySiteList', siteListId] as const,
  // `params` is required, not optional: omitting it builds a `[..., undefined]` key
  // that prefix-matches nothing the table ever fetched under.
  bySiteList: (siteListId: string, params: SiteListsControllerFindSitesForSiteListParams | undefined) =>
    [...sitesKeys.bySiteListAll(siteListId), params] as const,
  detail: (siteId: string) => [...sitesKeys.all, siteId, 'detail'] as const,
  siteLists: (siteId: string) => [...sitesKeys.all, siteId, 'siteLists'] as const,
  checkNmiExists: (nmi: string) => [...sitesKeys.all, 'checkNmiExists', nmi] as const,
};

// Queries
export const useSites = (params?: SitesControllerFindAllParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: sitesKeys.list(params),
    queryFn: () => sitesControllerFindAll(params),
    placeholderData: (prev) => prev,
    enabled: options?.enabled,
  });
};

export const useSitesBySiteList = (siteListId: string, params?: SiteListsControllerFindSitesForSiteListParams) => {
  return useQuery({
    queryKey: sitesKeys.bySiteList(siteListId, params),
    queryFn: () => siteListsControllerFindSitesForSiteList(siteListId, params),
    enabled: !!siteListId,
    placeholderData: (prev) => prev,
    // Refetch on mount. The listener attaching used to force this, and without it
    // the default 5-minute staleTime shows a cached list to someone coming back
    // from editing those very sites. Not staleTime: 0 — that would also refetch
    // this 400KB response on every window focus.
    refetchOnMount: 'always',
    // RYO syncs finish in a Cloud Task, so nothing pushes the result back. Poll
    // only while a row is mid-sync, and stop as soon as none are.
    refetchInterval: (query) =>
      query.state.data?.items?.some((site) => site.ryoSync?.status === 'pending') ? 5000 : false,
  });
};

export const useSite = (siteId: string) => {
  return useQuery({
    queryKey: sitesKeys.detail(siteId),
    queryFn: () => sitesControllerFindOne(siteId),
    enabled: !!siteId,
  });
};

export const useSiteListsForSite = (siteId: string) => {
  return useQuery({
    queryKey: sitesKeys.siteLists(siteId),
    queryFn: () => sitesControllerFindSiteListsForSite(siteId),
    enabled: !!siteId,
  });
};

// Mutations
export const useCreateSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSiteDto) => sitesControllerCreate(data),
    onSuccess: (newSite) => {
      // Invalidate and refetch sites list
      queryClient.invalidateQueries({ queryKey: sitesKeys.list() });

      // Optionally add to cache
      if (newSite.id) {
        queryClient.setQueryData(sitesKeys.detail(newSite.id), newSite);
      }
    },
  });
};

export const useUpdateSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteId, data }: { siteId: string; data: UpdateSiteDto }) => sitesControllerUpdate(siteId, data),
    onMutate: async ({ siteId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: sitesKeys.detail(siteId) });

      // Snapshot the previous value
      const previousSite = queryClient.getQueryData(sitesKeys.detail(siteId));

      // Optimistically update to the new value with deep merge
      if (previousSite) {
        queryClient.setQueryData(sitesKeys.detail(siteId), (old: SiteDto | undefined) => {
          if (!old) return old;

          // Deep merge for nested objects
          const merged = { ...old } as SiteDto;
          for (const [key, value] of Object.entries(data)) {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
              // Merge nested objects
              const oldValue = old[key as keyof SiteDto];
              (merged as unknown as Record<string, unknown>)[key] = {
                ...(oldValue && typeof oldValue === 'object' ? oldValue : {}),
                ...value,
              };
            } else {
              // Direct assignment for primitives and arrays
              (merged as unknown as Record<string, unknown>)[key] = value;
            }
          }
          return merged;
        });
      }

      // Return a context object with the snapshotted value
      return { previousSite, siteId };
    },
    onSuccess: (updatedSite, { siteId }) => {
      // Update with the actual server response
      queryClient.setQueryData(sitesKeys.detail(siteId), updatedSite);

      // Invalidate the list to ensure consistency
      queryClient.invalidateQueries({ queryKey: sitesKeys.list() });
    },
    onError: (_err, { siteId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousSite) {
        queryClient.setQueryData(sitesKeys.detail(siteId), context.previousSite);
      }
    },
  });
};

export const useDeleteSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (siteId: string) => sitesControllerRemove(siteId),
    onSuccess: (_, siteId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: sitesKeys.detail(siteId) });

      // Invalidate the list
      queryClient.invalidateQueries({ queryKey: sitesKeys.list() });
    },
  });
};

export const useMoveSiteToList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteId,
      fromSiteListId,
      toSiteListId,
    }: {
      siteId: string;
      fromSiteListId: string;
      toSiteListId: string;
    }) => sitesControllerMoveSiteToList(siteId, { fromSiteListId, toSiteListId }),
    onSuccess: (_, { siteId, fromSiteListId, toSiteListId }) => {
      // Invalidate both site lists
      queryClient.invalidateQueries({ queryKey: sitesKeys.bySiteListAll(fromSiteListId) });
      queryClient.invalidateQueries({ queryKey: sitesKeys.bySiteListAll(toSiteListId) });

      // Invalidate site lists query for this site
      queryClient.invalidateQueries({ queryKey: sitesKeys.siteLists(siteId) });
    },
  });
};

export const useBatchMoveSitesToList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteIds,
      fromSiteListId,
      toSiteListId,
    }: {
      siteIds: string[];
      fromSiteListId: string;
      toSiteListId: string;
    }) => sitesControllerBatchMoveSitesToList({ siteIds, fromSiteListId, toSiteListId }),
    onSuccess: (_, { siteIds, fromSiteListId, toSiteListId }) => {
      // Invalidate both site lists
      queryClient.invalidateQueries({ queryKey: sitesKeys.bySiteListAll(fromSiteListId) });
      queryClient.invalidateQueries({ queryKey: sitesKeys.bySiteListAll(toSiteListId) });

      // Invalidate site lists query for all moved sites
      siteIds.forEach((siteId) => {
        queryClient.invalidateQueries({ queryKey: sitesKeys.siteLists(siteId) });
      });
    },
  });
};

// CRM Import Queries & Mutations
export const useCheckNmiExists = (nmi: string, enabled: boolean) => {
  return useQuery({
    queryKey: sitesKeys.checkNmiExists(nmi),
    queryFn: () => sitesControllerCheckNmiExists({ nmi }),
    enabled: enabled && nmi.length >= 10,
    retry: false,
    staleTime: 30 * 1000, // 30 seconds - short since data can change
    gcTime: 60 * 1000,
  });
};

export const useImportSiteFromCrm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ImportSiteFromCrmDto) => sitesControllerImportFromCrm(data),
    onSuccess: (site) => {
      // Invalidate sites list
      queryClient.invalidateQueries({ queryKey: sitesKeys.list() });

      // Add to cache
      queryClient.setQueryData(sitesKeys.detail(site.id), site);
    },
  });
};
